import { Art } from "@components/Collection/Art";
import Buttons from "@components/Collection/Buttons";
import CollectionTitles from "@components/Collection/CollectionTitles";
import LBox from "@components/LBox";
import { getViewPort } from "@utils/web/config";
import React from "react";
import { useWindowDimensions } from "react-native";
import {
  SharedValue,
  useAnimatedStyle,
  useDerivedValue,
  interpolate,
  Extrapolation,
} from "react-native-reanimated";

// Note: This value is calculated based on the Figma design target (iPhone 12 mini)
const IMAGE_HEIGHT_SCALE_FACTOR = 180 / 812;
// Offset at which the image has shrunk to nothing.
const IMAGE_SCALE_END = 500;

type ListHeaderProps = {
  blurhash?: string | null | (string | null)[];
  id: string;
  image?: string | null | (string | null | undefined)[];
  isFetching: boolean;
  isOwner: boolean;
  isPublic: boolean;
  onPress: () => void;
  subtitle?: string;
  subtitleNumberOfLines?: number;
  title?: string;
  type: "album" | "playlist";
  hasPrivateSong?: boolean;
  y: SharedValue<number>;
};

const ListHeader = ({
  blurhash,
  id,
  image,
  isFetching,
  isOwner,
  isPublic,
  onPress,
  subtitle,
  subtitleNumberOfLines,
  title,
  type,
  y,
  hasPrivateSong,
}: ListHeaderProps) => {
  const dimensions = useWindowDimensions();
  const viewPort = getViewPort(dimensions);
  const imageHeight = viewPort.height * IMAGE_HEIGHT_SCALE_FACTOR;

  // A style that returns a transform counts as changed on every run, and each
  // change commits a layout pass on the UI thread. Clamping the offset first
  // stops the style running once the image has scrolled out of range.
  const imageOffset = useDerivedValue(() =>
    Math.min(Math.max(y.value, 0), IMAGE_SCALE_END),
  );

  const imageContainerStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          scale: interpolate(
            imageOffset.value,
            [0, imageHeight, IMAGE_SCALE_END],
            [1, 0.5, 0],
            Extrapolation.CLAMP,
          ),
        },
        {
          translateY: interpolate(
            imageOffset.value,
            [0, imageHeight],
            [0, imageHeight / 2],
            Extrapolation.CLAMP,
          ),
        },
      ],
    };
  });

  if (!id || !type) {
    return null;
  }

  return (
    <>
      <LBox gap="s2" pt="s3">
        <Art
          id={id}
          style={imageContainerStyle}
          size={imageHeight}
          imageUrl={image}
          type={type}
          blurhash={blurhash}
          hasPrivateSong={hasPrivateSong}
          borderRadius="l"
        />

        <LBox my="s3">
          <CollectionTitles
            id={id}
            onPress={onPress}
            screen={"library"}
            subtitle={subtitle}
            subtitleNumberOfLines={subtitleNumberOfLines}
            title={title}
            type={type}
          />
        </LBox>
      </LBox>
      <LBox
        bg="screenBg"
        borderTopLeftRadius="xl"
        borderTopRightRadius="xl"
        flexGrow={1}
        gap="s3"
        pt="s3"
        px="s4"
      >
        <Buttons
          id={id}
          isFetching={isFetching}
          isOwner={isOwner}
          isPublic={isPublic}
          type={type}
        />

        <LBox bg="faintWhite" gradientDirection="horizontal" height={1} />
      </LBox>
    </>
  );
};

export default ListHeader;
